import { BaseInstrumentInfo } from "./BaseInstrumentInfo";
import { memo,useEffect,useMemo,useState } from "react";
import { CoveredMatrixGroup,CoveredMatrixMarketWatch,CoveredMatrixOrderItemTableReport } from "src/Interfaces/coveredMatrixOrder";
import { getCoveredMatrixPortfolioImbalance,getCoveredMatrixOrderItemsTable,getCoveredMatrixMarketWatch,getCoveredMatrixTseLinks } from "src/Requests/coveredMatrixOrder";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { tableRefreshInterval } from "../model";
import { getMatrixItemExtraTypeUI,MatrixItemTableContent } from "./ItemTable";

export const OrderItemDetails=memo(({ group,tableVersion,baseName,accountName }: { group: CoveredMatrixGroup; tableVersion: number; baseName: string; accountName: string; }) => {
  const [baseMarketWatch,setBaseMarketWatch]=useState<CoveredMatrixMarketWatch|null>(null);
  const [portfolioCoveredCallImbalance,setPortfolioCoveredCallImbalance]=useState<number|null>(null);
  const [tseLinks,setTseLinks]=useState<Record<string,string>>({});
  const itemSearchParameters=useMemo(() => ({ groupId: group.id }),[group.id]);
  useEffect(() => {
    let disposed=false;
    setBaseMarketWatch(null);
    setPortfolioCoveredCallImbalance(null);
    setTseLinks({});
    const normalizeInstrumentId=(value: string) => value.trim().toUpperCase();
    const getByInstrumentId=<T,>(values: Record<string,T>,instrumentId: string): T|undefined => {
      const normalizedId=normalizeInstrumentId(instrumentId);
      return Object.entries(values).find(([key]) => normalizeInstrumentId(key)===normalizedId)?.[1];
    };
    let refreshing=false;
    const refreshMarket=async () => {
      if(refreshing) return;
      refreshing=true;
      await Promise.all([getCoveredMatrixMarketWatch([group.baseIsin])
        .then((marketWatch) => {
          if(disposed) return;
          setBaseMarketWatch(getByInstrumentId(marketWatch,group.baseIsin)||null);
        })
        .catch((error) => { if(!disposed) packetErrorHandler(error); }),
      getCoveredMatrixPortfolioImbalance(group.id)
        .then((result) => {
          if(!disposed) setPortfolioCoveredCallImbalance(result.imbalance);
        })
        .catch((error) => { if(!disposed) packetErrorHandler(error); })]);
      refreshing=false;
    };
    refreshMarket();
    const timer=window.setInterval(refreshMarket,tableRefreshInterval);
    getCoveredMatrixTseLinks([group.baseIsin])
      .then((repositoryLinks) => {
        if(disposed) return;
        setTseLinks({
          ...Object.fromEntries(Object.entries(repositoryLinks)
            .filter(([,link]) => Boolean(link))
            .flatMap(([isin,link]) => [[isin,link],[normalizeInstrumentId(isin),link]])),
        });
      })
      .catch(packetErrorHandler);
    return () => { disposed=true; window.clearInterval(timer); };
  },[group.id,group.baseIsin,tableVersion]);
  const baseBest=baseMarketWatch;
  const hasWideBaseVolume=[baseBest?.buyVolume,baseBest?.sellVolume]
    .some((value) => value!==null&&value!==undefined&&Math.abs(Number(value))>=1_000_000);
  const baseTseLink=tseLinks[group.baseIsin]||tseLinks[group.baseIsin.trim().toUpperCase()];
  const format=(value?: number|null) => value==null? "-":value.toLocaleString();
  return <div className={`grid min-w-0 grid-cols-1 items-start gap-2 ${hasWideBaseVolume? "xl:grid-cols-[340px_minmax(0,1fr)]":"xl:grid-cols-[270px_minmax(0,1fr)]"}`} dir="rtl">
    <BaseInstrumentInfo name={baseName} marketWatch={baseMarketWatch} referencePrice={group.manualBaseReferencePrice} tseLink={baseTseLink}
      account={accountName||"-"} holding={format(group.baseHoldingVolume)} imbalance={format(portfolioCoveredCallImbalance)} />
    <div className="matrix-order-items-table min-w-0 w-full self-start">
      <TableTemplate<{ groupId: number },CoveredMatrixOrderItemTableReport>
        key={group.id}
        searchParameters={itemSearchParameters}
        refreshTableTime={tableRefreshInterval}
        silentRefreshKey={tableVersion}
        showIsLoading={false}
        hideUntilResponse
        withSort
        getDataTable={getCoveredMatrixOrderItemsTable}
        getExtraTypeUI={getMatrixItemExtraTypeUI}
        tableWrapperClassname="!mx-0 !w-full [&>div]:!p-0"
        TableUI={(props) => <MatrixItemTableContent {...props} />}
      />
    </div>
  </div>;
});
